/**
 * R5 — Flat selection (design/redesign_v3/Auth & Onboarding.dc.html frames
 * "Flat select" + "Onboarding desktop"). Wing pills → flat grid (claimed =
 * disabled/subdued). Wired: GET /api/societies/:id/wings and
 * /api/societies/:id/flats?wing=. On continue, routes to /onboarding/verify
 * with societyId + flatConfigId + flatCode (+ societyName carried through).
 * Desktop (≥1024): steps rail + centered content, 8-column flat grid.
 */
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { Pressable, View, useWindowDimensions } from 'react-native';

import { radius, space } from '@bero/tokens';
import { bool, extractItems, num, str } from '@/components/gate/api';
import { Icon } from '@/features/account-admin/icons';
import { BottomBar } from '@/features/community/bits';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, EmptyState, Screen, Skeleton, TopBar } from '@/ui';

import { DesktopShell, ONBOARDING_DESKTOP_BREAKPOINT, StepsRail } from './_shell';

type Wing = { id: number; code: string; name: string };
type Flat = { code: string; claimed: boolean; configId: number };
type LoadState = 'loading' | 'ready' | 'error';

function parseWings(body: unknown): Wing[] {
  return extractItems<unknown>(body, 'wings').map((raw, i) => {
    const code = str(raw, 'code') ?? String(i + 1);
    return { id: num(raw, 'id') ?? i, code, name: str(raw, 'name') ?? '' };
  });
}

function parseFlats(body: unknown): Flat[] {
  return extractItems<unknown>(body, 'flats')
    .map((raw) => ({
      code: str(raw, 'code') ?? '',
      claimed: bool(raw, 'claimed') ?? false,
      configId: num(raw, 'configId', 'id') ?? 0,
    }))
    .filter((f) => f.code);
}

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function WingPill({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  const { colors } = useTheme();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      onPress={onPress}
      style={{
        minHeight: 44, paddingHorizontal: space.s4, borderRadius: radius.full, borderWidth: 1,
        alignItems: 'center', justifyContent: 'center',
        borderColor: active ? colors.primary500 : colors.border,
        backgroundColor: active ? colors.primary500 : 'transparent',
      }}
    >
      <AppText variant="bodySm" weight="600" style={{ color: active ? colors.canvas0 : colors.textSecondary }}>
        {label}
      </AppText>
    </Pressable>
  );
}

function FlatCell({ flat, selected, onPress }: { flat: Flat; selected: boolean; onPress: () => void }) {
  const { colors } = useTheme();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected, disabled: flat.claimed }}
      disabled={flat.claimed}
      onPress={onPress}
      style={{
        flex: 1, aspectRatio: 1, minHeight: 44, borderRadius: radius.md, borderWidth: 1,
        alignItems: 'center', justifyContent: 'center',
        borderColor: selected ? colors.primary500 : colors.borderSoft,
        backgroundColor: selected ? colors.primary50 : flat.claimed ? colors.canvas2 : colors.canvas0,
        opacity: flat.claimed ? 0.55 : 1,
      }}
    >
      <AppText
        variant="bodySm"
        weight="500"
        mono
        style={{ color: selected ? colors.primary600 : flat.claimed ? colors.textTertiary : colors.textPrimary }}
      >
        {flat.code}
      </AppText>
    </Pressable>
  );
}

function useWingsAndFlats(societyId: string | undefined) {
  const [wings, setWings] = React.useState<Wing[]>([]);
  const [wingsState, setWingsState] = React.useState<LoadState>('loading');
  const [selectedWing, setSelectedWing] = React.useState<string | null>(null);
  const [wingsTick, setWingsTick] = React.useState(0);

  const [flats, setFlats] = React.useState<Flat[]>([]);
  const [flatsState, setFlatsState] = React.useState<LoadState>('loading');
  const [selected, setSelected] = React.useState<Flat | null>(null);
  const [flatsTick, setFlatsTick] = React.useState(0);

  React.useEffect(() => {
    if (!societyId) return;
    let cancelled = false;
    void Promise.resolve().then(() => {
      if (cancelled) return;
      setWingsState('loading');
    });
    (async () => {
      try {
        const body = await api.get<unknown>(`/api/societies/${societyId}/wings`);
        if (cancelled) return;
        const ws = parseWings(body);
        setWings(ws);
        setWingsState('ready');
        setSelectedWing((prev) => prev ?? ws[0]?.code ?? null);
      } catch {
        if (cancelled) return;
        setWings([]);
        setWingsState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [societyId, wingsTick]);

  React.useEffect(() => {
    if (!societyId || !selectedWing) return;
    let cancelled = false;
    void Promise.resolve().then(() => {
      if (cancelled) return;
      setFlatsState('loading');
      setSelected(null);
    });
    (async () => {
      try {
        const body = await api.get<unknown>(`/api/societies/${societyId}/flats?wing=${encodeURIComponent(selectedWing)}`);
        if (cancelled) return;
        setFlats(parseFlats(body));
        setFlatsState('ready');
      } catch {
        if (cancelled) return;
        setFlats([]);
        setFlatsState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [societyId, selectedWing, flatsTick]);

  return {
    wings, wingsState, selectedWing, setSelectedWing, retryWings: () => setWingsTick((n) => n + 1),
    flats, flatsState, selected, setSelected, retryFlats: () => setFlatsTick((n) => n + 1),
  };
}

export default function OnboardingFlatScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { width } = useWindowDimensions();
  const { societyId, societyName } = useLocalSearchParams<{ societyId?: string; societyName?: string }>();
  const {
    wings, wingsState, selectedWing, setSelectedWing, retryWings,
    flats, flatsState, selected, setSelected, retryFlats,
  } = useWingsAndFlats(societyId);

  if (!societyId) {
    return (
      <Screen scroll={false}>
        <TopBar title="" onBack={() => router.back()} bordered={false} />
        <EmptyState
          icon={<Icon name="building" size={40} color={colors.textTertiary} />}
          title={t('onb_flat_no_soc_title')}
          body={t('onb_flat_no_soc_body')}
          cta={t('onb_choose_society')}
          onCta={() => router.replace('/onboarding/society')}
        />
      </Screen>
    );
  }

  const continueHref = () => {
    if (!selected) return;
    const qs = new URLSearchParams({ societyId, flatConfigId: String(selected.configId), flatCode: selected.code });
    if (societyName) qs.set('societyName', societyName);
    router.push(`/onboarding/verify?${qs.toString()}`);
  };

  const desktop = width >= ONBOARDING_DESKTOP_BREAKPOINT;
  const cols = desktop ? 8 : 4;
  const rows = chunk(flats, cols);

  const wingsRow = (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2 }}>
      {wingsState === 'loading' ? (
        [0, 1, 2].map((i) => <Skeleton key={i} w={72} h={44} r={radius.full} />)
      ) : wingsState === 'error' ? (
        <Button title={t('retry')} kind="tertiary" onPress={retryWings} />
      ) : (
        wings.map((w) => (
          <WingPill key={w.id} label={w.name || t('onb_wing_n', { code: w.code })} active={selectedWing === w.code} onPress={() => setSelectedWing(w.code)} />
        ))
      )}
    </View>
  );

  const flatsGrid = flatsState === 'loading' ? (
    <View style={{ gap: space.s2 }}>
      {[0, 1, 2].map((i) => (
        <View key={i} style={{ flexDirection: 'row', gap: space.s2 }}>
          {Array.from({ length: cols }).map((_, j) => <Skeleton key={j} w={`${Math.floor(90 / cols)}%`} h={56} r={radius.md} />)}
        </View>
      ))}
    </View>
  ) : flatsState === 'error' ? (
    <EmptyState
      icon={<Icon name="alert-triangle" size={40} color={colors.textTertiary} />}
      title={t('onb_flat_err_title')}
      body={t('onb_check_connection')}
      cta={t('retry')}
      onCta={retryFlats}
    />
  ) : flats.length === 0 ? (
    <EmptyState
      icon={<Icon name="home" size={40} color={colors.textTertiary} />}
      title={t('onb_flat_none_title')}
      body={t('onb_flat_none_body')}
    />
  ) : (
    <View style={{ gap: space.s2 }}>
      {rows.map((row, i) => (
        <View key={i} style={{ flexDirection: 'row', gap: space.s2 }}>
          {row.map((f) => (
            <FlatCell key={f.code} flat={f} selected={selected?.code === f.code} onPress={() => setSelected(f)} />
          ))}
        </View>
      ))}
    </View>
  );

  if (desktop) {
    return (
      <DesktopShell
        rail={<StepsRail steps={[
          { label: t('onb_step_society'), sub: societyName ?? t('onb_step_society_chosen'), state: 'done' },
          { label: t('onb_step_flat'), sub: t('onb_step_flat_sub'), state: 'active' },
          { label: t('onb_step_verify'), sub: t('onb_step_verify_sub'), state: 'upcoming' },
        ]} />}
      >
        <View style={{ flex: 1 }}>
          <AppText variant="displayLg">{t('onb_flat_title')}</AppText>
          <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s2 }}>
            {(societyName ?? t('onb_your_society')) + ' · ' + t('onb_flat_claimed_locked')}
          </AppText>
          <View style={{ marginTop: space.s6 }}>{wingsRow}</View>
          <View style={{ marginTop: space.s6, maxWidth: 820 }}>{flatsGrid}</View>
          <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: space.s3, marginTop: 'auto', paddingTop: space.s8 }}>
            <Button title={t('onb_back')} kind="secondary" onPress={() => router.back()} />
            <Button title={selected ? t('onb_flat_continue_with', { code: selected.code }) : t('onb_flat_select_cta')} disabled={!selected} onPress={continueHref} />
          </View>
        </View>
      </DesktopShell>
    );
  }

  return (
    <Screen scroll={false}>
      <TopBar title="" onBack={() => router.back()} right={societyName ? <AppText variant="bodySm" tone="tertiary">{societyName}</AppText> : undefined} bordered={false} />
      <View style={{ paddingHorizontal: space.s4, paddingTop: space.s2 }}>
        <AppText variant="headingLg">{t('onb_flat_title')}</AppText>
        <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s2 }}>
          {t('onb_flat_sub_mobile')}
        </AppText>
        <View style={{ marginTop: space.s5 }}>{wingsRow}</View>
      </View>

      <Screen padded style={{ paddingTop: space.s4, paddingBottom: space.s4 }}>
        {flatsGrid}
      </Screen>

      <BottomBar>
        <Button
          title={selected ? t('onb_flat_continue_with', { code: selected.code }) : t('onb_flat_select_cta')}
          size="lg"
          disabled={!selected}
          onPress={continueHref}
        />
      </BottomBar>
    </Screen>
  );
}
